import React, { useState } from 'react';
import { Row, Col, Card, Typography, Statistic, DatePicker, Select, Space, List, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { Line, Bar } from '@ant-design/plots';
import dayjs from 'dayjs';
import { salesDashboardStats } from '../../mock/salesDashboard';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const SalesDashboard: React.FC = () => {
  const { t } = useTranslation(['common', 'sales']);
  const screens = Grid.useBreakpoint();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().subtract(30, 'days'), dayjs()]);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year' | 'custom'>('month');

  // Stats data
  const stats = [
    {
      title: t('sales:dashboard.stats.newLeads'),
      value: salesDashboardStats.newLeads,
    },
    {
      title: t('sales:dashboard.stats.currentOpportunities'),
      value: salesDashboardStats.currentOpportunities,
    },
    {
      title: t('sales:dashboard.stats.closedCustomers'),
      value: salesDashboardStats.closedCustomers,
    },
    {
      title: t('sales:dashboard.stats.newBookings'),
      value: salesDashboardStats.newBookings,
    },
  ];

  // Sales trends data
  const salesTrendsData = [
    { month: 'Jan', value: 100 },
    { month: 'Feb', value: 120 },
    { month: 'Mar', value: 150 },
    { month: 'Apr', value: 180 },
    { month: 'May', value: 200 },
    { month: 'Jun', value: 220 },
  ];

  // Transform sales trends data for the line chart
  const lineChartData = salesTrendsData.map(item => ({
    date: item.month,
    value: item.value,
  }));

  const lineChartConfig = {
    data: lineChartData,
    xField: 'date',
    yField: 'value',
    smooth: true,
    point: true,
    height: screens.xs ? 220 : 300,
  };

  // Customers by country data (dummy)
  const customersByCountryData = [
    { country: 'USA', value: 35 },
    { country: 'UK', value: 25 },
    { country: 'Canada', value: 20 },
    { country: 'Australia', value: 15 },
    { country: 'Germany', value: 10 },
  ];

  const barChartConfig = {
    data: customersByCountryData,
    xField: 'value',
    yField: 'country',
    seriesField: 'country',
    legend: false,
    color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'],
    height: screens.xs ? 220 : 300,
  };

  // Recent activity data
  const recentActivity = [
    {
      type: 'lead',
      message: t('sales:dashboard.activity.newLead'),
      timestamp: new Date(),
    },
    {
      type: 'opportunity',
      message: t('sales:dashboard.activity.opportunityCreated'),
      timestamp: new Date(),
    },
    {
      type: 'customer',
      message: t('sales:dashboard.activity.customerConverted'),
      timestamp: new Date(),
    },
    {
      type: 'booking',
      message: t('sales:dashboard.activity.newBooking'),
      timestamp: new Date(),
    },
  ];

  const timeRangeOptions = [
    { label: t('timeRanges.week'), value: 'week' },
    { label: t('timeRanges.month'), value: 'month' },
    { label: t('timeRanges.year'), value: 'year' },
    { label: t('timeRanges.custom'), value: 'custom' },
  ];

  const handleTimeRangeChange = (value: 'week' | 'month' | 'year' | 'custom') => {
    setTimeRange(value);
    if (value !== 'custom') {
      const end = dayjs();
      const start = value === 'week' 
        ? end.subtract(7, 'days')
        : value === 'month'
          ? end.subtract(30, 'days')
          : end.subtract(365, 'days');
      setDateRange([start, end]);
    }
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>{t('sales:dashboard.title')}</Title>
        </Col>
        <Col>
          <Space>
            <Select 
              value={timeRange} 
              onChange={handleTimeRangeChange}
              style={{ width: 120 }}
            >
              {timeRangeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            {timeRange === 'custom' && (
              <RangePicker
                value={dateRange}
                onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                allowClear={false}
              />
            )}
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        {stats.map((stat, index) => (
          <Col span={6} key={index}>
            <Card>
              <Statistic title={stat.title} value={stat.value} />
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={16}>
          <Card title={t('sales:dashboard.charts.salesTrends')}>
            <Line {...lineChartConfig} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title={t('sales:dashboard.charts.customersByCountry')}>
            <Bar {...barChartConfig} />
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title={t('sales:dashboard.sections.recentActivity')}>
        <List
          dataSource={recentActivity}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                title={item.message}
                description={item.timestamp.toLocaleDateString()}
              />
            </List.Item>
          )}
        />
      </Card>

      <Card style={{ marginTop: '24px' }}>
        <Title level={4} style={{ marginBottom: '16px'}}>{t('sales:dashboard.activityOverview')}</Title>
        <Text>
          {t('sales:dashboard.chartsPlaceholder')}
        </Text>
      </Card>
    </div>
  );
};

export default SalesDashboard; 